import { useEffect, useState } from 'react';
import { isAndroid, isIOS, isMacOs, isMobileOnly, isTablet, isWindows } from 'react-device-detect';

import { Dashboard } from '@src/main/modules/common/dashboard/domain/Dashboard';
import { LoginStatisticsCreate } from '@src/main/modules/common/statistics/domain/Statistics';
import { StatisticsRepository } from '@src/main/modules/common/statistics/domain/StatisticsRepository';
import { findUser } from '@src/main/modules/common/user/application/find/findUser';
import { User } from '@src/main/modules/common/user/domain/User';
import { UserRepository } from '@src/main/modules/common/user/domain/UserRepository';

import { useError } from '@src/main/ui/components/common/error/hooks/useError';

export const useUserUseCases = (
  userRepository: UserRepository,
  statisticsRepository: StatisticsRepository,
) => {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [users, setUsers] = useState<User[] | undefined>(undefined);
  const { addError } = useError();

  const toggleFavorite = async (dashboard: Dashboard) => {
    setUser((prevUser) => {
      if (prevUser) {
        return { ...prevUser, favorites: addToFavorites(prevUser, dashboard) };
      }
      return prevUser;
    });
  };

  const addRecentlyOpened = async (dashboard: Dashboard) => {
    setUser((prevUser) => {
      if (prevUser) {
        return { ...prevUser, recentlyOpened: addToRecentlyOpened(prevUser, dashboard) };
      }
      return prevUser;
    });
  };

  const addAdminRole = async (userId: string, userToAdmin: User) => {
    try {
      let updatedRoles: string[];
      if (userToAdmin.roles.includes('ADMIN')) {
        updatedRoles = userToAdmin.roles.filter((role) => role !== 'ADMIN');
        await userRepository.addAdminRole(userId, ['USER']);
      } else {
        updatedRoles = [...userToAdmin.roles, 'ADMIN'];
        await userRepository.addAdminRole(userId, updatedRoles);
      }

      setUsers(
        users?.map((userFromList) => {
          if (userFromList.id === userId) {
            return {
              ...userFromList,
              roles: updatedRoles,
            };
          }
          return userFromList;
        }),
      );
    } catch {
      const customError = new Error(
        `Error ${userToAdmin.roles.includes('ADMIN') ? 'removing' : 'adding'} admin role`,
      );
      addError(customError);
    }
  };

  useEffect(() => {
    reloadUserInfo(userRepository, setUser).catch(addError);
    reloadUsersInfo(userRepository, setUsers);
  }, [addError, userRepository]);

  useEffect(() => {
    if (user) {
      userRepository.saveUser(user);
    }
  }, [user, userRepository, statisticsRepository, addError]);

  useEffect(() => {
    if (user?.id) {
      const createUserLogin: LoginStatisticsCreate = {
        userId: user?.id,
        userName: user?.name,
        deviceType: getDeviceType(),
        operatingSystem: getOS(),
      };
      createLoginStatistics(statisticsRepository, createUserLogin, addError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return {
    user,
    users,
    toggleFavorite,
    addRecentlyOpened,
    addAdminRole,
  };
};

const reloadUserInfo = async (userRepository: UserRepository, setUser: (user: User) => void) => {
  const user: User = await findUser(userRepository);
  setUser(user);
};

const reloadUsersInfo = async (
  userRepository: UserRepository,
  setUsers: (users: User[]) => void,
) => {
  const users: User[] = await userRepository.findAllUsers();
  setUsers(users);
};

const addToFavorites = (user: User, dashboard: Dashboard): string[] => {
  if (user.favorites.includes(dashboard.id)) {
    return user.favorites.filter((favorite) => favorite !== dashboard.id);
  }
  return [...user.favorites, dashboard.id];
};

const addToRecentlyOpened = (user: User, dashboard: Dashboard): string[] => {
  if (user.recentlyOpened.includes(dashboard.id)) {
    return [dashboard.id, ...user.recentlyOpened.filter((recently) => recently !== dashboard.id)];
  }

  return [dashboard.id, ...user.recentlyOpened].slice(0, 5);
};

const createLoginStatistics = async (
  statisticsRepository: StatisticsRepository,
  loginStatisticsCreate: LoginStatisticsCreate,
  addError: (error: Error) => void,
) => {
  try {
    await statisticsRepository.saveLoginStatistics(loginStatisticsCreate);
  } catch {
    addError(new Error('Error saving login statistics'));
  }
};

const getDeviceType = () => {
  if (isMobileOnly) {
    return 'Mobile';
  }
  if (isTablet) {
    return 'Tablet';
  }
  return 'Desktop';
};

const getOS = () => {
  if (isIOS) {
    return 'iPhone';
  }
  if (isAndroid) {
    return 'Android';
  }
  if (isWindows) {
    return 'Windows';
  }
  if (isMacOs) {
    return 'MacOS';
  }
  return 'Other';
};
